import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { AxiosInstance } from 'axios'
import type { ApiItem, AuthUser } from '@yuksalish/domain-types'
import { formatApiError } from '@yuksalish/api-client'

const TOKEN_STORAGE_KEY = 'yuksalish.access_token'

export interface LoginCredentials {
  email: string
  password: string
}

interface LoginResponse {
  access_token: string
  expires_at: string
  token_type: 'Bearer'
  user: AuthUser
}

function readStoredToken(): string | null {
  return typeof window === 'undefined'
    ? null
    : window.localStorage.getItem(TOKEN_STORAGE_KEY)
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(readStoredToken())
  const user = ref<AuthUser | null>(null)
  const isBootstrapping = ref(Boolean(token.value))
  const isSubmitting = ref(false)
  const errorMessage = ref<string | null>(null)
  const isAuthenticated = computed(() => Boolean(token.value && user.value))

  function setSession(nextToken: string, nextUser: AuthUser): void {
    token.value = nextToken
    user.value = nextUser
    window.localStorage.setItem(TOKEN_STORAGE_KEY, nextToken)
  }

  function clearSession(): void {
    token.value = null
    user.value = null
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY)
    }
  }

  async function bootstrap(client: AxiosInstance): Promise<void> {
    if (!token.value) {
      isBootstrapping.value = false
      return
    }

    try {
      const response = await client.get<ApiItem<AuthUser>>('/auth/me')
      user.value = response.data.data
    } catch {
      clearSession()
    } finally {
      isBootstrapping.value = false
    }
  }

  async function login(
    client: AxiosInstance,
    credentials: LoginCredentials,
  ): Promise<boolean> {
    isSubmitting.value = true
    errorMessage.value = null

    try {
      const response = await client.post<ApiItem<LoginResponse>>('/auth/login', {
        ...credentials,
        token_name: 'Standalone frontend',
      })
      setSession(response.data.data.access_token, response.data.data.user)
      return true
    } catch (error: unknown) {
      errorMessage.value = formatApiError(error)
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  async function logout(client: AxiosInstance): Promise<void> {
    try {
      if (token.value) {
        await client.post('/auth/logout')
      }
    } finally {
      clearSession()
    }
  }

  return {
    token,
    user,
    isAuthenticated,
    isBootstrapping,
    isSubmitting,
    errorMessage,
    bootstrap,
    login,
    logout,
    clearSession,
  }
})
